import { Stack } from "@fluentui/react";
import {
  Card,
  CardHeader,
  Text,
  Tooltip,
  SkeletonItem,
} from "@fluentui/react-components";
import { Info16Regular } from "@fluentui/react-icons";
import { ReactNode } from "react";
import { useStyles as useManageStatisticsStyles } from "../ManageStatistics.styles";
import {
  metricCardHeaderStackStyles,
  metricCardHeaderTokens,
  useStyles,
} from "./MetricCard.styles";

interface IMetricCardProps {
  icon: ReactNode;
  title: string;
  tooltip: string;
  value: number | string | undefined;
  children?: ReactNode;
  isLoading?: boolean;
}

export const MetricCard = ({
  icon,
  title,
  tooltip,
  value,
  children,
  isLoading = false,
}: IMetricCardProps): JSX.Element => {
  const styles = useManageStatisticsStyles();
  const metricCardStyles = useStyles();

  return (
    <Card className={`${styles.card} ${metricCardStyles.metricCard}`}>
      <CardHeader
        className={metricCardStyles.cardHeader}
        header={
          <Stack
            horizontal
            verticalAlign="center"
            tokens={metricCardHeaderTokens}
            styles={metricCardHeaderStackStyles}
          >
            <div className={metricCardStyles.metricIcon}>{icon}</div>
            <Text
              weight="semibold"
              size={300}
              className={metricCardStyles.title}
            >
              {title}
            </Text>
          </Stack>
        }
        action={
          <Tooltip content={tooltip} relationship="label">
            <Info16Regular className={metricCardStyles.tooltipIcon} />
          </Tooltip>
        }
      />
      <div
        className={`${styles.cardContent} ${metricCardStyles.metricContent}`}
      >
        {isLoading ? (
          <SkeletonItem size={32} className={metricCardStyles.skeletonValue} />
        ) : (
          <>
            {(typeof value === "number" || typeof value === "string") && (
              <Text size={600} weight="bold" className={metricCardStyles.value}>
                {value !== undefined ? value : "--"}
              </Text>
            )}
            {children}
          </>
        )}
      </div>
    </Card>
  );
};

interface IMetricCardGridProps {
  icon: ReactNode;
  title: string;
  tooltip: string;
  children: ReactNode;
  isLoading?: boolean;
}

export const MetricCardGrid = ({
  icon,
  title,
  tooltip,
  children,
  isLoading = false,
}: IMetricCardGridProps) => {
  const styles = useManageStatisticsStyles();
  const metricCardStyles = useStyles();

  return (
    <Card className={`${styles.card} ${metricCardStyles.gridCard}`}>
      <CardHeader
        className={metricCardStyles.gridCardHeader}
        header={
          <Stack
            horizontal
            verticalAlign="center"
            tokens={metricCardHeaderTokens}
            styles={metricCardHeaderStackStyles}
          >
            <div className={metricCardStyles.metricIcon}>{icon}</div>
            <Text
              weight="semibold"
              size={300}
              className={metricCardStyles.title}
            >
              {title}
            </Text>
          </Stack>
        }
        action={
          <Tooltip content={tooltip} relationship="label">
            <Info16Regular className={metricCardStyles.tooltipIcon} />
          </Tooltip>
        }
      />
      <div className={metricCardStyles.gridContent}>
        {isLoading ? (
          <>
            <SkeletonItem
              size={16}
              className={metricCardStyles.gridSkeletonWide}
            />
            <SkeletonItem
              size={16}
              className={metricCardStyles.gridSkeletonMedium}
            />
            <SkeletonItem
              size={16}
              className={metricCardStyles.gridSkeletonNarrow}
            />
          </>
        ) : (
          children
        )}
      </div>
    </Card>
  );
};
